import React, { useEffect, useState } from "react";
import { useParams, useLocation, useNavigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { getLayoutById, fetchPublicPlots } from "../services/api";
import { formatUrl } from "../utils/config";
import { formatPrice } from "../utils/format";
import { stripHtml } from "../utils/text";
import PlotCard from "../components/PlotCard";

const LayoutDetails = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [layout, setLayout] = useState(location.state?.layout || location.state?.layoutData);
  const [loading, setLoading] = useState(!(location.state?.layout || location.state?.layoutData));
  const [mainImage, setMainImage] = useState("");

  const [layoutPlots, setLayoutPlots] = useState([]);
  const [plotsLoading, setPlotsLoading] = useState(true);

  useEffect(() => {
    const loadLayout = async () => {
      if (!layout) {
        try {
          const res = await getLayoutById(id);
          const data = res?.payload || res;
          setLayout(data);
          setMainImage(data?.thumbnail || "");
        } catch (error) {
          console.error("Error loading layout:", error);
          navigate("/layouts", { replace: true });
        } finally {
          setLoading(false);
        }
      } else {
        setMainImage(layout?.thumbnail || "");
      }
    };

    loadLayout();
  }, [id, layout, navigate]);

  // Load plots within this layout
  useEffect(() => {
    const loadPlots = async () => {
      if (!id) return;
      setPlotsLoading(true);
      try {
        const res = await fetchPublicPlots(0, 50, { layoutId: id });
        const page = res?.payload?.page || [];
        setLayoutPlots(Array.isArray(page) ? page : []);
      } catch (error) {
        console.error("Error loading plots for layout:", error);
        setLayoutPlots([]);
      } finally {
        setPlotsLoading(false);
      }
    };

    loadPlots();
  }, [id]);

  const createMarkup = (html) => ({ __html: html });
  const isUrl = (val) => typeof val === "string" && /^https?:\/\//i.test(val);
  const getMapSrc = (coords) => {
    if (!coords) return null;
    if (isUrl(coords)) return coords;
    const value = String(coords).trim();
    // Build a Google Maps embed URL from raw coordinates/text
    return `https://www.google.com/maps?q=${encodeURIComponent(value)}&output=embed`;
  };

  if (loading) {
    return (
      <div className="space-top space-extra-bottom">
        <div className="container">
          <div className="row">
            <div className="col-12 text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!layout) {
    return (
      <div className="space-top space-extra-bottom">
        <div className="container">
          <div className="row">
            <div className="col-12 text-center py-5">
              <h3>Layout not found</h3>
              <p>The layout you are looking for does not exist or has been removed.</p>
              <button className="th-btn" onClick={() => navigate("/layouts")}>Back to Layouts</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{layout?.name || "Layout Details"}</title>
        <meta name="description" content={layout?.description || "Layout details"} />
      </Helmet>

      <div
        className="breadcumb-wrapper"
        style={{
          backgroundImage: "url('/assets/img/main/m1.jpeg')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="container">
          <div className="breadcumb-content">
            <h1 className="breadcumb-title">{layout?.name}</h1>
            <ul className="breadcumb-menu">
              <li>
                <Link to="/layouts">Layouts</Link>
              </li>
              <li>{layout?.name}</li>
            </ul>
          </div>
        </div>
      </div>

      <section className="space-top space-extra-bottom">
        <div className="container">
          <div className="row g-4">
            <div className="col-lg-8">
              <div className="property-main-image mb-4">
                <img
                  src={formatUrl(mainImage) || "/assets/img/default-offer.png"}
                  alt={layout?.name || "Layout"}
                  className="img-fluid rounded w-100"
                  style={{ height: "500px", objectFit: "cover" }}
                  onError={(e) => (e.currentTarget.src = "/assets/img/default-offer.png")}
                />
              </div>

              {Array.isArray(layout?.photos) && layout.photos.length > 0 && (
                <div className="property-thumbnails mb-5">
                  <div className="row g-3">
                    <div className="col-auto">
                      <button
                        className={`thumbnail-btn ${mainImage === layout.thumbnail ? "active" : ""}`}
                        onClick={() => setMainImage(layout.thumbnail)}
                      >
                        <img
                          src={formatUrl(layout.thumbnail)}
                          alt="Thumbnail"
                          className="img-fluid"
                          style={{ width: "80px", height: "60px", objectFit: "cover" }}
                        />
                      </button>
                    </div>
                    {layout.photos.map((photo, index) => (
                      <div className="col-auto" key={index}>
                        <button
                          className={`thumbnail-btn ${mainImage === photo ? "active" : ""}`}
                          onClick={() => setMainImage(photo)}
                        >
                          <img
                            src={formatUrl(photo)}
                            alt={`Thumbnail ${index + 1}`}
                            className="img-fluid"
                            style={{ width: "80px", height: "60px", objectFit: "cover" }}
                          />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="d-flex my-3">
                {layout?.design && (
                  <div className="estate-thumbnail flex-fill">
                    <video
                      width={"100%"}
                      height={"200px"}
                      src={formatUrl(layout.design)}
                      controls={true}
                      volume={0}
                      autoPlay={false}
                    />
                  </div>
                )}

                {layout?.coordinates && (
                  <div className="estate-thumbnail ps-2 flex-fill">
                    <iframe
                      src={getMapSrc(layout.coordinates)}
                      width="100%"
                      height={"200px"}
                      style={{ border: "0" }}
                      allowFullScreen
                      loading="lazy"
                    />
                  </div>
                )}
              </div>

              <div className="property-details mb-5">
                <h2 className="h3 mb-3">{layout?.name}</h2>
                <p className="text-muted mb-4">
                  <i className="fas fa-map-marker-alt text-primary me-2"></i>
                  {(layout?.district ? layout.district + ", " : "")}
                  {layout?.lga || layout?.city || ""}
                  {layout?.state ? ", " + layout.state : ""}
                </p>

                <div className="property-features mb-4">
                  <div className="row g-3">
                    {layout?.sizeSqm && (
                      <div className="col-md-3 col-6">
                        <div className="feature-box text-center p-3 border rounded">
                          <i className="fas fa-ruler-combined fa-2x text-primary mb-2"></i>
                          <h6 className="mb-0">{layout.sizeSqm} sqm</h6>
                        </div>
                      </div>
                    )}
                    {/* {layout?.acquisitionType && (
                      <div className="col-md-3 col-6">
                        <div className="feature-box text-center p-3 border rounded">
                          <i className="fas fa-file-signature fa-2x text-primary mb-2"></i>
                          <h6 className="mb-0">{layout.acquisitionType}</h6>
                        </div>
                      </div>
                    )} */}
                  </div>
                </div>

                {layout?.description && (
                  <div className="property-description mb-5">
                    <h4 className="h5 mb-3">Description</h4>
                    <p>{stripHtml(layout.description)}</p>
                  </div>
                )}

                {layout?.features && (
                  <div className="property-features-list mb-5">
                    <h4 className="h5 mb-3">Features</h4>
                    <div className="row g-1" dangerouslySetInnerHTML={createMarkup(layout.features)} />
                  </div>
                )}

                {/* Plots in this Layout */}
                <div className="property-layout-plots mb-5">
                  <h4 className="h5 mb-4">Plots in this Layout</h4>
                  {plotsLoading ? (
                    <div className="text-center py-4">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  ) : layoutPlots.length === 0 ? (
                    <p className="text-muted">No plots found in this layout.</p>
                  ) : (
                    <>
                      <div className="row gy-4 mt-2">
                        {layoutPlots.slice(0, 2).map((plot) => (
                          <PlotCard key={plot.id} plot={plot} columnClass="col-md-6" />
                        ))}
                      </div>
                      {layoutPlots.length > 2 && (
                        <div className="text-center mt-4">
                          <Link
                            to={`/layouts/${id}/plots`}
                            state={{ layout }}
                            className="th-btn"
                          >
                            View All Plots
                            <i className="fas fa-arrow-right ms-2"></i>
                          </Link>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className="col-lg-4">
              <div className="property-sidebar">
                <div className="estate-info bg-light p-4 rounded mb-4">
                  <h4 className="h5 mb-3">Layout Information</h4>
                  {layout?.thumbnail && (
                    <div className="estate-thumbnail mb-3">
                      <img
                        src={formatUrl(layout.thumbnail)}
                        alt={layout?.name}
                        className="img-fluid rounded"
                        style={{ height: "150px", width: "100%", objectFit: "cover" }}
                      />
                    </div>
                  )}

                  <ul className="list-unstyled mb-0">
                    {layout?.name && (
                      <li className="d-flex justify-content-between py-2">
                        <span>Name:</span>
                        <strong>{layout.name}</strong>
                      </li>
                    )}
                    {layout?.acquisitionType && (
                      <li className="d-flex justify-content-between py-2">
                        <span>Acquisition Type:</span>
                        <strong>{layout.acquisitionType}</strong>
                      </li>
                    )}
                    {/* {(layout?.district || layout?.lga || layout?.state || layout?.city) && (
                      <li className="d-flex justify-content-between py-2">
                        <span>Location:</span>
                        <strong>
                          {(layout?.district ? layout.district + ", " : "")}
                          {layout?.lga || layout?.city || ""}
                          {layout?.state ? ", " + layout.state : ""}
                        </strong>
                      </li>
                    )} */}
                    {layout?.status && (
                      <li className="d-flex justify-content-between py-2">
                        <span>Status:</span>
                        <strong className={(layout.status === "AVAILABLE" || layout.status === "ACTIVE") ? "text-success" : "text-danger"}>
                          {layout.status}
                        </strong>
                      </li>
                    )}
                    {(typeof layout?.totalPlots === "number") && (
                      <li className="d-flex justify-content-between py-2">
                        <span>Total Plots:</span>
                        <strong>{layout.totalPlots}</strong>
                      </li>
                    )}
                    {(typeof layout?.availablePlots === "number") && (
                      <li className="d-flex justify-content-between py-2">
                        <span>Available Plots:</span>
                        <strong>{layout.availablePlots}</strong>
                      </li>
                    )}
                    {(typeof layout?.soldPlots === "number") && (
                      <li className="d-flex justify-content-between py-2">
                        <span>Sold Plots:</span>
                        <strong>{layout.soldPlots}</strong>
                      </li>
                    )}
                    {(layout?.acquisitionCost !== undefined && layout?.acquisitionCost !== null && layout?.acquisitionCost !== "") && (
                      <li className="d-flex justify-content-between py-2">
                        <span>Acquisition Cost:</span>
                        <strong>{formatPrice(Number(layout.acquisitionCost) || 0)}</strong>
                      </li>
                    )}
                    {Array.isArray(layout?.approvals) && (
                      <li className="d-flex justify-content-between py-2">
                        <span>Approvals:</span>
                        <strong>{layout.approvals.length}</strong>
                      </li>
                    )}
                  </ul>
                  {Array.isArray(layout?.approvals) && layout.approvals.length > 0 && (
                    <div className="mt-2">
                      <small className="text-muted">Approvals available.</small>
                    </div>
                  )}
                  {layout?.planUrl && (
                    <div className="mt-3">
                      <a
                        href={formatUrl(layout.planUrl)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-outline-primary w-100 text-decoration-none"
                      >
                        <i className="fas fa-file-alt me-2"></i> View Plan
                      </a>
                    </div>
                  )}
                </div>

                <div className="property-actions d-flex flex-column gap-3 mb-3">
                  <Link to="/contact" className="btn btn-primary w-100 text-decoration-none">
                    <i className="fas fa-envelope me-2"></i> Contact Us
                  </Link>
                  <Link to="tel:+2347055558942" className="btn btn-warning w-100 text-decoration-none">
                    <i className="fas fa-phone me-2"></i> Call Us
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default LayoutDetails;
